import { useRef, useState, type FormEvent } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { BookingFull, DocumentType } from './types'
import { formatBookingMoveOption } from './helpers'

export default function DocumentUploadDialog({
  open,
  onOpenChange,
  shipmentId,
  bookings,
  pathFor,
  initialDocumentType = 'si',
  initialBookingId = '',
  lockBooking = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  shipmentId: string
  bookings: BookingFull[]
  pathFor: (path: string) => string
  initialDocumentType?: DocumentType
  initialBookingId?: string
  lockBooking?: boolean
}) {
  const [uploadForm, setUploadForm] = useState({
    file: null as File | null,
    documentType: initialDocumentType,
    bookingId: initialBookingId,
    documentNumber: '',
  })
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleUploadSubmit(e: FormEvent) {
    e.preventDefault()
    if (!uploadForm.file) {
      toast.error('Please select a file')
      return
    }

    setIsUploading(true)
    const formData = new FormData()
    formData.append('shipment_document[file]', uploadForm.file)
    formData.append('shipment_document[document_type]', uploadForm.documentType)
    formData.append('shipment_document[booking_id]', uploadForm.bookingId || '')
    formData.append('shipment_document[shipment_id]', shipmentId)
    if (uploadForm.documentType === 'bl' && uploadForm.documentNumber.trim()) {
      formData.append('shipment_document[document_number]', uploadForm.documentNumber.trim())
    }

    router.post(pathFor('/shipment_documents'), formData, {
      onSuccess: () => {
        onOpenChange(false)
        setUploadForm({
          file: null,
          documentType: initialDocumentType,
          bookingId: initialBookingId,
          documentNumber: '',
        })
        setIsUploading(false)
      },
      onError: () => {
        toast.error('Failed to upload document')
        setIsUploading(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a document and optionally tag it to a booking.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUploadSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-[12px] font-medium text-muted-foreground">File:</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
              className="block w-full text-[12px] text-muted-foreground file:mr-4 file:rounded file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-[12px] file:font-medium file:text-gray-700 hover:file:bg-gray-200 cursor-pointer"
            />
            {uploadForm.file && (
              <p className="mt-1 text-[11px] text-green-600">{uploadForm.file.name}</p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-[12px] font-medium text-muted-foreground">Document Type:</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setUploadForm({ ...uploadForm, documentType: 'si' })}
                className={`flex-1 rounded border px-3 py-2 text-[12px] cursor-pointer ${uploadForm.documentType === 'si' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-200 text-muted-foreground'}`}
              >
                SI (Shipping Instructions)
              </button>
              <button
                type="button"
                onClick={() => setUploadForm({ ...uploadForm, documentType: 'bl' })}
                className={`flex-1 rounded border px-3 py-2 text-[12px] cursor-pointer ${uploadForm.documentType === 'bl' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-200 text-muted-foreground'}`}
              >
                BL (Bill of Lading)
              </button>
              <button
                type="button"
                onClick={() => setUploadForm({ ...uploadForm, documentType: 'other' })}
                className={`flex-1 rounded border px-3 py-2 text-[12px] cursor-pointer ${uploadForm.documentType === 'other' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-200 text-muted-foreground'}`}
              >
                Other
              </button>
            </div>
          </div>

          {uploadForm.documentType === 'bl' && (
            <div>
              <label className="mb-1.5 block text-[12px] font-medium text-muted-foreground">
                BL Number <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={uploadForm.documentNumber}
                onChange={(e) => setUploadForm({ ...uploadForm, documentNumber: e.target.value })}
                placeholder="e.g. HLCU1234567890"
                className="w-full rounded border px-3 py-2 text-[12px]"
              />
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-[12px] font-medium text-muted-foreground">
              Booking:
            </label>
            <select
              aria-label="Booking"
              value={uploadForm.bookingId}
              onChange={(e) => setUploadForm({ ...uploadForm, bookingId: e.target.value })}
              disabled={lockBooking}
              className={`w-full rounded border px-3 py-2 text-[12px] ${lockBooking ? 'border-gray-200 bg-gray-50 text-foreground opacity-100' : 'cursor-pointer'}`}
            >
              {!lockBooking && <option value="">Leave untagged</option>}
              {bookings.map(b => (
                <option key={b.id} value={b.id}>
                  {formatBookingMoveOption(b)}
                </option>
              ))}
            </select>
          </div>

          <DialogFooter className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!uploadForm.file || isUploading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
